import {
  ArrowUp, Copy, Eye, EyeOff, GripVertical, MousePointer2, Trash2, UploadCloud, X,
} from "lucide-react";
import type { EditorField, EditorObject } from "@prototypical/editor-contract";
import type { ImageUploadState } from "../preview-bridge";

export function InspectPanel({
  field, object, value, attributes, upload, onUpload, onCancelUpload, onChange, onMeta, onOperation, allowFocal = true, allowImageUrl = true,
}: {
  field?: EditorField;
  object?: EditorObject;
  value: string;
  attributes?: { href?: string; alt?: string; focalX?: number; focalY?: number };
  upload: ImageUploadState | null;
  onUpload: (file: File) => void;
  onCancelUpload: () => void;
  onChange: (value: string) => void;
  onMeta: (key: "href" | "alt" | "focalX" | "focalY", value: string) => void;
  onOperation: (operation: string) => void;
  allowFocal?: boolean;
  allowImageUrl?: boolean;
}) {
  if (!field && !object) return <div className="editor-selection-empty"><MousePointer2 size={24} /><h3>Select something on the page</h3><p>Choose text, a link, an image, or a section on the canvas or in the Objects tree.</p></div>;
  if (!field && object) return (
    <>
      <section className="inspector-section selected-summary"><div className="editor-panel-label">Selected {object.kind}</div><h3>{object.label}</h3><code>{object.id}</code></section>
      {!!object.operations?.length && <section className="inspector-section"><div className="editor-panel-label">Structure actions</div><div className="operation-grid">{object.operations.map((operation) => {
        const Icon = operation === "move-up" ? ArrowUp : operation === "duplicate" ? Copy : operation === "hide" ? EyeOff : operation === "show" ? Eye : operation === "delete" ? Trash2 : GripVertical;
        return <button key={operation} onClick={() => onOperation(operation)}><Icon size={13} />{operation.replace("-", " ")}</button>;
      })}</div></section>}
    </>
  );
  if (!field) return null;
  const isImage = field.type === "image";
  const isLink = field.type === "link" || field.type === "url" || field.tag === "a";
  const chooseFile = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onUpload(file);
  };
  return (
    <>
      <section className="inspector-section selected-summary"><div className="editor-panel-label">Selected {isImage ? "image" : isLink ? "link" : "text"}</div><h3>{field.label}</h3><code>{field.path}</code></section>
      <section className="inspector-section">
        <label className="field-label">{isImage ? "Image URL" : isLink ? "Label" : "Content"}</label>
        {isImage ? <>
          <div
            className="image-upload-zone"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); chooseFile(event.dataTransfer.files); }}
          >
            {value && <img src={value} alt="" />}
            <UploadCloud size={20} />
            <strong>Drop a photo or choose a file</strong>
            <small>JPEG, PNG, WebP, or GIF · up to 5MB</small>
            <label className="editor-button">Choose image<input aria-label="Choose image" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => chooseFile(event.target.files)} /></label>
          </div>
          {upload?.status === "uploading" && <div className="image-upload-progress" role="status">
            <span style={{ width: `${upload.progress ?? 15}%` }} />
            <small>{upload.progress === null ? "Uploading…" : `Uploading… ${upload.progress}%`}</small>
            <button onClick={onCancelUpload} aria-label="Cancel upload"><X size={13} /></button>
          </div>}
          {upload?.status === "error" && <div className="image-upload-error" role="alert">
            <span>{upload.message}</span>
            <button onClick={() => onUpload(upload.file)}>Retry</button>
          </div>}
          {allowImageUrl && <details className="image-url-fallback"><summary>Use an image URL instead</summary><input type="url" value={value} onChange={(event) => onChange(event.target.value)} /></details>}
        </> : <textarea autoFocus value={value} onChange={(event) => onChange(event.target.value)} />}
        {isLink && <><label className="field-label">Destination</label><input type="url" value={attributes?.href ?? field.values?.href ?? field.metadata?.href ?? ""} onChange={(event) => onMeta("href", event.target.value)} /></>}
        {isImage && <><label className="field-label">Alternative text</label><input value={attributes?.alt ?? field.values?.alt ?? field.metadata?.alt ?? ""} onChange={(event) => onMeta("alt", event.target.value)} />{allowFocal && <div className="focal-grid"><label>Horizontal focal point<input type="range" min="0" max="100" value={(attributes?.focalX ?? field.metadata?.focalX ?? .5) * 100} onChange={(event) => onMeta("focalX", String(Number(event.target.value) / 100))} /></label><label>Vertical focal point<input type="range" min="0" max="100" value={(attributes?.focalY ?? field.metadata?.focalY ?? .5) * 100} onChange={(event) => onMeta("focalY", String(Number(event.target.value) / 100))} /></label></div>}</>}
        <small>Changes appear on the canvas immediately when supported by this site.</small>
      </section>
      {!!object?.operations?.length && <section className="inspector-section"><div className="editor-panel-label">Actions</div><div className="operation-grid">{object.operations.map((operation) => {
        const Icon = operation === "move-up" ? ArrowUp : operation === "duplicate" ? Copy : operation === "hide" ? EyeOff : operation === "show" ? Eye : operation === "delete" ? Trash2 : GripVertical;
        return <button key={operation} onClick={() => onOperation(operation)}><Icon size={13} />{operation.replace("-", " ")}</button>;
      })}</div></section>}
    </>
  );
}
